import { observable } from 'mobx'
import { getAppConfig, APPConfig } from 'config/appConfig'
import { IS_BROWSER, getLiveEventStatus, getFooterBynderImage } from 'src/utils'
import { CommonContainer } from 'src/models/Common'
import { getResourceFrom3rdPartyUrl } from 'src/api/localeResources'

/**
 * FooterContainer class that extends CommonContainer.
 * Represents the footer container component.
 */
class FooterContainer extends CommonContainer {
  @observable footerResponse = {}
  @observable nuskinLogoURL = ''
  @observable searchResults = null

  /**
   * Fetches footer data from the Content Service or a 3rd party URL based on config.
   * Transforms the response into the footer model used in the app.
   *
   * Async function that takes in component props.
   * Checks config to see if footer data should come from Content Service or external URL.
   * Constructs params for each data source, making call and transforming response.
   * Returns promise resolving to transformed footer data model.
   */
  getFooterData = async props => {
    const getFooterDataFromCS =
      APPConfig?.getAppConfig()?.enableFooterFromCS == 'true'

    const enableLiveEvent = getLiveEventStatus()
    const footerFileName =
      enableLiveEvent === 'true' ? 'liveEventFooter' : 'footer'

    const locale = (props?.queryParams?.locale || 'en_US').replace('_', '-')
    const csLoadParams = {
      endPointName: 'footerData',
      contextName: props.contextName,
      curLocaleFromUrl: locale,
    }
    const loadParams = {
      // endPointName: 'footerData',
      contextName: props.contextName,
      credentials: 'omit',
      isToIncludeCommomHeaders: false,
      // curLocaleFromUrl: props.context?.curLocaleFromUrl,
      deleteContentType: true,

      // new method props
      fileName: footerFileName,
      locale,
      domainName: getAppConfig().localeResourceFilesDomain || 'dev.nuskin.com',
    }

    let response = {}

    if (IS_BROWSER) {
      if (getFooterDataFromCS) {
        response = await this.fetchResponse(csLoadParams)
      } else {
        response = await getResourceFrom3rdPartyUrl(loadParams)
      }
    } else {
      if (props.context && !props.context.ssrData?.[props.contextName]) {
        if (getFooterDataFromCS) {
          props.context.load(this.fetchResponse(csLoadParams))
        } else {
          props.context.load(getResourceFrom3rdPartyUrl(loadParams))
        }
      }
    }

    if (this.isSuccessResponse(response)) {
      this.footerResponse = getFooterDataFromCS
        ? this.transformFooter(response)
        : response
    } else {
      console.error('Fn Error in Footer response ', response)
    }

    return this.footerResponse
  }

  /**
   * Fix: Add method to provide empty footer structure as fallback
   */
  getEmptyFooterStructure = () => ({
    navigation: [],
    socialMediaHeaderText: '',
    socialMediaList: [],
    affiliated_navigation_: { links: [] },
    detailsLinks: [],
    footerLogo: '',
  })

  /**
   * Transforms the footer response data into a model used in the application.
   * Fix: Add better error handling and data validation
   */
  transformFooter = response => {
    const transformModel = this.getEmptyFooterStructure()

    // Fix: Handle both entries array and direct entry object
    const entry = response?.entries?.[0] || response?.entry || response || {}

    if (!entry || Object.keys(entry).length === 0) {
      console.warn('Empty footer entry, returning default structure')
      return transformModel
    }

    try {
      // navigation model - Fix: Add null checks
      const navigationSections = entry.navigation_menu_sections || []
      navigationSections.forEach(section => {
        const sec = section.sections
        if (sec?.is_visible) {
          const navObj = {
            title: sec?.title || '',
            links: [],
          }
          const subLinks = sec?.sub_links || []
          subLinks.forEach(link => {
            navObj.links.push({
              name: link?.title || '',
              href: link?.href || '',
            })
          })
          transformModel.navigation.push(navObj)
        }
      })

      // social links model - Fix: Add null checks
      transformModel.socialMediaHeaderText = entry.social_link_title || ''
      const socialLinks = entry.social_links || []
      socialLinks.forEach(linkObj => {
        const links = linkObj.links
        if (links?.is_visible) {
          transformModel.socialMediaList.push({
            socialIconLink: links?.link?.href || '',
            socialIconUrl: links?.icon || '',
            socialIconUploaded: links?.icon_file?.url || '',
          })
        }
      })

      // affiliated_links (seal_section) - Fix: Add null checks
      const sealSection = entry.seal_section || []
      transformModel.affiliated_navigation_ = {
        links: sealSection
          .filter(seal => seal?.show)
          .map(seal => {
            let image = []
            try {
              image = seal.image ? JSON.parse(seal.image)?.image || [] : []
            } catch (e) {
              console.warn('Error parsing seal image:', e)
              image = []
            }
            return {
              icon: image,
              link: seal.redirect_link || {},
            }
          }),
      }

      const brandLogo = entry?.brand_logo
      if (brandLogo?.show) {
        const logoUrl = getFooterBynderImage({
          bynderImage: brandLogo?.footer_logo?.[0],
        })
        transformModel.footerLogo = logoUrl || ''
      }

      // privacy/legal links - Fix: Add null checks
      const legalLinks = entry.legal_links || []
      legalLinks.forEach(linkObj => {
        const legal = linkObj.legal_links
        if (legal?.show) {
          transformModel.detailsLinks.push({
            name: legal?.title_and_link?.title || '',
            href: legal?.title_and_link?.href || '',
          })
        }
      })
    } catch (error) {
      console.error('Error transforming footer data:', error)
    }

    return transformModel
  }
  getDSSearchResults = async props => {
    const params = {
      page: props.page || 1,
      size: props.size || 25,
    }
    const loadParams = {
      endPointName: 'DSSearch',
      postData: { ...props.payload },
      queryParams: params,
    }
    const response = await this.fetchResponse(loadParams)
    this.searchResults = response
    return this.searchResults
  }
}

const footerContainer = new FooterContainer()

export { footerContainer, FooterContainer }
export default footerContainer
